import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import Dropdown, {
  DropdownInfiniteScrollProps,
  DropdownItemProps,
  DropdownOption,
  DropdownOptionGroup,
  MultiItemProps,
} from 'src/components/Dropdown/Dropdown'
import EmptyDropdownMessage from 'src/components/EmptyDropdownMessage/EmptyDropdownMessage'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { css } from 'styled-components'

export interface CreditCardOptionDetails {
  creditCard: CreditCardListModel
  hideAmount: boolean
}

export type CreditCardOption = DropdownOption<CreditCardOptionDetails>
export const creditCardOption = (
  creditCard: CreditCardListModel,
  hideAmount?: boolean,
): CreditCardOption => ({
  value: creditCard?.id || '',
  details: {
    creditCard: creditCard,
    hideAmount: !!hideAmount,
  },
})

const CreditCardDropdownItem: React.FC<
  DropdownItemProps<CreditCardOptionDetails>
> = (props): React.ReactElement => {
  const translate = useTranslate()
  return (
    <div
      className={props.className}
      data-single-exact-pay-enabled={String(
        props.details?.creditCard?.features?.singleExactPayEnabled ?? false,
      )}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 2;
        height: 60px;
        ${props.isRoot &&
        css`
          height: 100%;
        `}
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
    >
      {props.details !== undefined && (
        <div
          css={css`
            display: flex;
            align-items: center;
            overflow: hidden;
          `}
        >
          {props.details.creditCard.isBudget && (
            <img
              css={css`
                width: 20px;
              `}
              src={props.details.creditCard.cardTypeImageSrc}
              alt={''}
            />
          )}
          {!props.details.creditCard.isBudget && (
            <img
              src={props.details.creditCard.cardImage.urls.small}
              css={css`
                width: 30px;
                border-radius: 3.37%/5.92%;
              `}
              alt={''}
            />
          )}
          <span
            id={'accountCreditCards'}
            css={css`
              margin-left: 10px;
              margin-right: 10px;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              ${getFontCss(
                16,
                themeConstants.colors.font_primary,
                styleConstants.fontFamilies.medium,
              )};
              ${props.isSelected &&
              css`
                ${getFontCss(
                  16,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            `}
          >
            {props.details.creditCard.truncatedDisplayName}
          </span>
        </div>
      )}
      {props.disabled ? (
        <span
          css={css`
            color: ${styleConstants.colors.red};
          `}
        >
          <div>{translate('Action Required')}</div>
        </span>
      ) : (
        props.details?.creditCard.isBudget &&
        !props.details.hideAmount && (
          <span
            data-unissued-budget={props.details.creditCard.unissuedAmount}
            css={css`
              color: ${themeConstants.colors.highlight};
              ${(props.details.creditCard.isInactive ||
                props.details.creditCard.isClosed) &&
              css`
                color: ${styleConstants.colors.red};
              `}
            `}
          >
            {props.details.creditCard.unissuedAmountLabel}
          </span>
        )
      )}
    </div>
  )
}

const CreditCardMultiItem: React.FC<MultiItemProps<CreditCardOptionDetails>> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 2;
        height: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
    >
      {props.options.length === 1 &&
        (props.options[0].details?.creditCard.truncatedDisplayName ||
          props.options[0].value)}
      {props.options.length > 1 &&
        translate('${selectedOption}, +${count} more', {
          selectedOption:
            props.options[0].details?.creditCard.truncatedDisplayName ||
            props.options[0].value,
          count: `${props.options.length - 1}`,
        })}
    </div>
  )
}

const getCreditCardOptionFilterText = (option: CreditCardOption): string =>
  option.details !== undefined
    ? option.details.creditCard.displayName
    : option.value

export type CreditCardDropdownInfiniteScrollProps = DropdownInfiniteScrollProps
interface CreditCardDropdownProps {
  className?: string
  optionGroups: Array<DropdownOptionGroup<CreditCardOption>>
  baseValues?: Set<string>
  selectedValues: Set<string>
  isMulti: boolean
  chooseOption: (val: string) => void
  placeholder?: string
  fieldLabel?: React.ReactNode
  errors?: Array<React.ReactElement | string>
  isStatic?: boolean
  onResetClick?: () => void
  infiniteScrollProps?: CreditCardDropdownInfiniteScrollProps
  selectedOptions?: CreditCardOption[]
  id?: string
  allowNoneProps?: {
    NoneDropdownItem: React.FC<{ className?: string }>
    deselectAllOptions: () => void
  }
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  tooltip?: React.ReactElement
}

const CreditCardDropdown: React.FC<CreditCardDropdownProps> = (
  props,
): React.ReactElement => {
  const noSourceImageSrc = useColorOrGrayscaleImage(ImageNames.NoSource)
  const translate = useTranslate()
  return (
    <Dropdown
      id={props.id}
      className={props.className}
      fieldLabel={props.fieldLabel}
      DropdownItem={CreditCardDropdownItem}
      MultiItem={CreditCardMultiItem}
      optionGroups={props.optionGroups}
      isMulti={props.isMulti}
      baseValues={props.baseValues}
      selectedValues={props.selectedValues}
      chooseOption={props.chooseOption}
      placeholder={props.placeholder}
      errors={props.errors}
      getFilterText={getCreditCardOptionFilterText}
      customEmptyDropdownMessage={
        <EmptyDropdownMessage
          imageSrc={noSourceImageSrc}
          subtitle={
            <div>
              {translate(
                'Try checking for typos or search for a different source.',
              )}
            </div>
          }
        />
      }
      isStatic={props.isStatic}
      selectedOptions={props.selectedOptions}
      onResetClick={props.onResetClick}
      infiniteScrollProps={props.infiniteScrollProps}
      allowNoneProps={props.allowNoneProps}
      showUpdates={props.showUpdates}
      hideWasOnUpdate={props.hideWasOnUpdate}
      tooltip={props.tooltip}
    />
  )
}

export default CreditCardDropdown
